// @flow
import api from 'api'
import Spinner from 'components/Spinner'
import { useForm } from 'hooks/useForm'
import usePermissions from 'hooks/usePermissions'
import * as React from 'react'
import { toast } from 'react-toastify'
import Button from 'ui/atoms/Button'
import Flex from 'ui/atoms/Flex'
import Text from 'ui/atoms/Text'
import Tooltip from 'ui/atoms/Tooltip'
import ButtonIcon from 'ui/ButtonIcon'
import Icons from 'ui/Icons'
import ModalDialog from 'ui/ModalDialog'

type Props = {
  controlsDisabled: ?boolean,
  resourceEnabled: boolean,
  resourceId: string | number,
}

function FormControlsDuplicateButton({
  controlsDisabled,
  resourceEnabled,
  resourceId,
}: Props): React.Node {
  const { canCreatePacketForm } = usePermissions()
  const [isCopyingForm, setIsCopyingForm] = React.useState(false)
  const [showModal, setShowModal] = React.useState(false)

  const { fetchFormPage } = useForm()

  const onCloneForm = React.useCallback(
    async (formId) => {
      setIsCopyingForm(true)
      try {
        await api.cloneForm(formId)
        await fetchFormPage()
        toast.success('Form duplicated successfully.')
      } catch (error) {
        if (error?.response?.data?.detail) {
          toast.error(error?.response?.data?.detail)
        } else {
          toast.error('Failed to duplicate form.')
        }
      } finally {
        setIsCopyingForm(false)
        setShowModal(false)
      }
    },
    [fetchFormPage]
  )

  const renderButton = React.useCallback(
    () => (
      <ButtonIcon
        rounded
        className="ml-2"
        disabled={controlsDisabled || !canCreatePacketForm || !resourceEnabled}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          setShowModal(true)
        }}
      >
        <Icons.Copy size="4" />
      </ButtonIcon>
    ),
    [canCreatePacketForm, controlsDisabled, resourceEnabled]
  )

  const renderContent = React.useCallback(() => {
    if (canCreatePacketForm) {
      return (
        <Tooltip label="Duplicate form">
          <p>{renderButton()}</p>
        </Tooltip>
      )
    }

    return (
      <Tooltip
        enabled={!canCreatePacketForm}
        label="Duplicate form - Limited Permissions"
      >
        <p>{renderButton()}</p>
      </Tooltip>
    )
  }, [canCreatePacketForm, renderButton])

  return (
    <>
      {renderContent()}

      {showModal && (
        <div
          className="cursor-default text-black"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
        >
          <ModalDialog
            disableOutsideClick
            hideCloseButton
            opened
            onClose={() => {
              setShowModal(false)
            }}
            title="Duplicate Form"
            width={400}
          >
            <Text>Are you sure you want to duplicate this form?</Text>
            <Flex className="mt-4 px-4" justify="between">
              <Button
                $primary
                disabled={isCopyingForm}
                onClick={async (e) => {
                  // $FlowFixMe
                  e.stopPropagation()
                  // $FlowFixMe
                  e.preventDefault()
                  await onCloneForm(resourceId)
                }}
              >
                {isCopyingForm && (
                  <Spinner color="rgba(255, 255, 255, 1)" size={20} />
                )}
                Duplicate
              </Button>
              <Button
                disabled={isCopyingForm}
                onClick={(e) => {
                  // $FlowFixMe
                  e.stopPropagation()
                  // $FlowFixMe
                  e.preventDefault()
                  setShowModal(false)
                }}
              >
                Cancel
              </Button>
            </Flex>
          </ModalDialog>
        </div>
      )}
    </>
  )
}

export default FormControlsDuplicateButton
